import {
  IFormJBPHPContactChangeRequest,
  IFormJBPHPContactRoles,
  JBPHPContactChangeRequestStatus,
} from 'legacy/lut/classes/models/partner360/IFormHPContacts'
import { HPContactsDuplicateDialog } from 'legacy/lut/components/widgets/public/partner360/common/hpContacts/hpContactsDuplicateDialog/HPContactsDuplicateDialog'
import { HpContactsTicketDeleteDialog } from 'legacy/lut/components/widgets/public/partner360/common/hpContacts/hpContactsTicketDeleteDialog/HpContactsTicketDeleteDialog'
import { HpContactsTicketNotNewDialog } from 'legacy/lut/components/widgets/public/partner360/common/hpContacts/hpContactsTicketNotNewDialog/HpContactsTicketNotNewDialog'
import { saveHPContactChangeRequest } from 'legacy/lut/components/widgets/public/partner360/helpers/hpContactRoles/saveHPContactChangeRequest'
import { useHpContacts } from 'legacy/lut/components/widgets/public/partner360/hooks/hpContacts/useHpContacts'
import { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'

import { Nullable } from 'clickNgo/models/utils'
import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { RenderWhen } from 'cng-common/components/renderWhen'
import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { Flex } from 'core-components/library/flex'
import { ModalDialog } from 'core-components/library/modalDialog'
import { Tooltip } from 'core-components/library/tooltip'
import { selectPublishingMode } from 'core/store/selectors/selectPublishingMode'
import { HPContactRequestEditor } from 'partner360/features/hpContacts'
import { removeHpContactRequest } from 'partner360/services/hpContacts/removeHpContactRequest'
import { TRANSLATION_KEYS } from 'partner360/widgets/ramManagerAssignment/translations'

interface IHpContactsActionsProps {
  contact: IFormJBPHPContactChangeRequest
  partnerId: string
  leadPbmId: number
  allRoles: IFormJBPHPContactRoles[]
  crudKey: string
  fetchPartnerRoles: (value: boolean) => Promise<IFormJBPHPContactRoles[]>
  onUpdateWizard: () => void
  setIsLoading: (flag: boolean) => void
  setIsDeleting: (num: Nullable<number>) => void
}

export const HpContactsActions = ({
  contact,
  fetchPartnerRoles,
  partnerId,
  leadPbmId,
  crudKey,
  allRoles,
  onUpdateWizard,
  setIsLoading,
  setIsDeleting,
}: IHpContactsActionsProps) => {
  const [showContactTicketDialog, setShowContactTicketDialog] = useState(false)
  const [currentHPContact, setCurrentHPContact] = useState<IFormJBPHPContactChangeRequest>()
  const [isDuplicationCheckInProgress, setDuplicationCheckInProgress] = useState(false)
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false)
  const [showTicketDeleteDialog, setShowTicketDeleteDialog] = useState(false)
  const [showTicketNotNewDialog, setShowTicketNotNewDialog] = useState(false)

  const { jbpHpContactChangeRequestCrud, resetCrudStorage, getContactsByEmail } = useHpContacts({
    crudKey: crudKey,
    partnerId,
  })

  const isEditMode = useSelector(selectPublishingMode)

  const onEditSalesTeamRecord = useCallback(() => {
    setCurrentHPContact({
      ...contact,
      RequestAction: 'Update',
    })
    setShowContactTicketDialog(true)
  }, [contact])

  const onDeleteSalesTeamRecord = useCallback(() => {
    setCurrentHPContact({
      ...contact,
      RequestAction: 'Delete',
    })
    setShowContactTicketDialog(true)
  }, [contact])

  const onDeleteTicket = useCallback(() => {
    if (contact.TicketStatus === JBPHPContactChangeRequestStatus.New) {
      setCurrentHPContact({
        ...contact,
        RequestAction: 'DeleteTicket',
      })
      setShowTicketDeleteDialog(true)
    } else {
      setShowTicketNotNewDialog(true)
    }
  }, [contact])

  const handleDeleteHPContact = useCallback(async () => {
    setIsLoading(true)
    setShowTicketDeleteDialog(false)
    await removeHpContactRequest({
      crud: jbpHpContactChangeRequestCrud,
      entryId: currentHPContact?.TicketId.toString() || '-1',
    })
    setIsDeleting(currentHPContact?.EntryId || null)
    resetCrudStorage()
    await fetchPartnerRoles(true)
    onUpdateWizard()
  }, [
    setIsLoading,
    jbpHpContactChangeRequestCrud,
    currentHPContact?.TicketId,
    currentHPContact?.EntryId,
    setIsDeleting,
    resetCrudStorage,
    fetchPartnerRoles,
    onUpdateWizard,
  ])

  return (
    <div>
      <LoaderOverlay isVisible={isDuplicationCheckInProgress} />

      <RenderWhen condition={isEditMode}>
        <Flex alignItems={'center'} gap={'xs'}>
          <RenderWhen condition={!contact.TicketId}>
            <Tooltip tipContent={() => <Translation text={TRANSLATION_KEYS.changeTooltip} />}>
              <Button
                iconButton={'kbase kbase-edit kbase-sm'}
                variant={'text'}
                onClick={onEditSalesTeamRecord}
              />
            </Tooltip>
          </RenderWhen>
          <RenderWhen condition={!contact.TicketId}>
            <Tooltip tipContent={() => <Translation text={TRANSLATION_KEYS.deleteTooltip} />}>
              <Button
                iconButton={'kbase kbase-delete kbase-sm'}
                variant={'text'}
                onClick={onDeleteSalesTeamRecord}
                color={'danger'}
              />
            </Tooltip>
          </RenderWhen>
          <RenderWhen
            condition={
              Boolean(contact.TicketId) &&
              contact.TicketStatus !== JBPHPContactChangeRequestStatus.WIP
            }
          >
            <Tooltip tipContent={() => <Translation text={TRANSLATION_KEYS.deleteTicketTooltip} />}>
              <Button
                iconButton={'kbase kbase-close kbase-sm'}
                variant={'text'}
                onClick={onDeleteTicket}
                color={'danger'}
              />
            </Tooltip>
          </RenderWhen>
        </Flex>
      </RenderWhen>

      <ModalDialog
        show={showContactTicketDialog}
        size={'sm'}
        content={HPContactRequestEditor}
        contentProps={{
          isVisible: showContactTicketDialog,
          currentHpContactRequest: (currentHPContact || {}) as IFormJBPHPContactChangeRequest,
          partnerId: partnerId as string,
          leadId: leadPbmId,
          currentRole: currentHPContact?.Roles || '',

          onApply: async (newRequest: IFormJBPHPContactChangeRequest) => {
            setShowContactTicketDialog(false)
            setDuplicationCheckInProgress(true)

            const { hasDuplicate } = await saveHPContactChangeRequest({
              newRequest,
              resetCrudStorage,
              getContactsByEmail,
              jbpHpContactChangeRequestCrud,
              fetchPartnerRoles,
              isUseCrudClone: true,
            })

            setDuplicationCheckInProgress(false)

            if (hasDuplicate) {
              setShowDuplicateDialog(true)
            } else {
              onUpdateWizard()
            }
          },
          onCancel: () => {
            setShowContactTicketDialog(false)
          },
        }}
        onClose={() => setShowContactTicketDialog(false)}
      />
      <HPContactsDuplicateDialog
        isShow={showDuplicateDialog}
        onClose={() => setShowDuplicateDialog(false)}
      />

      <HpContactsTicketNotNewDialog
        isShow={showTicketNotNewDialog}
        onClose={() => setShowTicketNotNewDialog(false)}
      />
      <HpContactsTicketDeleteDialog
        isShow={showTicketDeleteDialog}
        onClose={() => setShowTicketDeleteDialog(false)}
        handleDelete={handleDeleteHPContact}
      />
    </div>
  )
}
